import { segment } from '@actions/analytics';
import { useRouter } from 'next/router';
import {
  getQuizDetails,
  getCurrentQuizType,
  getCurrentSessionUID,
  getQuizSessionAnalytics
} from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { programmeTypes } from '@constants/index';
import { noOp } from '@constants/empty';
import { getPlusSubscriptionInfo } from '@selectors/subscription';
import { useSelector } from 'react-redux';
import isEmptyObject from '@utils/isEmptyObject';
import { getTestTabContextForQuizEvents } from '@cont/SeeAll/TestSeriesListing/testTab/testTabAnalytics';
import { getProctorReportProps } from '@cont/Quiz/Proctoring/proctorReportStore';
import { getIsCbtProgramme } from '../Details/utils';

export const getProctoringProps = (testSessionUID, quizSessionAnalytics) => {
  const backendReport = quizSessionAnalytics?.proctoring_report;

  if (backendReport) {
    return {
      proctoring_integrity_score: backendReport.integrityScore,
      proctoring_total_violations: backendReport.totalViolations,
      proctoring_session_status: backendReport.sessionStatus
    };
  }

  return getProctorReportProps(testSessionUID);
};

const useQuizSegmentAnalytics = () => {
  const router = useRouter();
  const { query } = router;
  const { quizUID, sessionUID } = query;

  const quizData = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const quizSessionAnalytics = useSelectorWithShallowEqual(
    getQuizSessionAnalytics(sessionUID)
  );
  // The attempt an event belongs to: the result route's sessionUID, or the
  // in-progress attempt while the test is still mounted.
  const currentSessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const testSessionUID = sessionUID || currentSessionUID;
  const { isSubjective } = useSelectorWithShallowEqual(getCurrentQuizType);
  const {
    subscription = {},
    expired_at,
    time_remaining
  } = useSelector(getPlusSubscriptionInfo(quizData?.goal_uid)) ?? {};

  if (isEmptyObject(quizData)) return noOp;

  return (actionKey, additionalProps = {}) => {
    const testTabContext = getTestTabContextForQuizEvents();

    segment.track(actionKey, {
      goal_name: quizData?.goal_name,
      goal_uid: quizData?.goal_uid,
      test_type: isSubjective ? 'Subjective' : 'Objective',
      programme_type:
        quizData?.collection?.programme?.programme_type ===
        programmeTypes.TESTSERIES
          ? 'Test Series'
          : 'Course',
      test_uid: testTabContext.test_uid || quizData?.uid,
      test_name: testTabContext.test_name || quizData?.title,
      test_series_uid:
        testTabContext.test_series_uid || quizData?.collection?.programme?.uid,
      test_series_title:
        testTabContext.test_series_title ||
        quizData?.collection?.programme?.name,
      is_live: !!(quizData?.quiz_over_in && quizData?.quiz_over_in > 0),
      is_nta_test: getIsCbtProgramme(quizData),
      subscription_end_date: expired_at,
      subscription_duration: subscription.duration,
      is_subscription_active: time_remaining > 0,
      ...getProctoringProps(testSessionUID, quizSessionAnalytics),
      ...testTabContext,
      ...additionalProps
    });
  };
};

export default useQuizSegmentAnalytics;
